import { ACTIVE_ITEMS } from '@constants/activeItems';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel } from '@mui/material';
import { RootContext } from 'components/RootContainer';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 10;

export const ChargeGroupField: FC<{
  value: number[] | null;
  onChange: (value: number[] | null) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation('reservation');

  const {
    HotelApi: {
      settings: {
        revenuManagement: {
          chargeGroup: { getAll },
        },
      },
    },
  } = useContext(RootContext);

  const { isLoading, data } = useQuery({
    queryKey: ['get-charge-groups'],
    queryFn: () => {
      return getAll({
        search: [ACTIVE_ITEMS],
      });
    },
  });

  const handleChange = (groupId: number | 'all') => {
    if (!data) return;

    const currentValue = value || [];

    if (groupId === 'all') {
      const allIds = data?.map(item => item?.id || 0);
      // If all items are currently selected, clear selection, otherwise select all
      onChange(currentValue.length === allIds.length ? [] : allIds);
      return;
    }

    const newValue = currentValue.includes(groupId)
      ? currentValue.filter(id => id !== groupId)
      : [...currentValue, groupId];
    onChange(newValue);
  };

  if (isLoading) {
    return <>...</>;
  }

  if (!data) {
    return <>...</>;
  }

  const currentValue = value || [];
  const allSelected = currentValue.length === data.length && data.length > 0;
  const hasSelection = currentValue.length > 0;
  const indeterminate = hasSelection && !allSelected;

  return (
    <Box
      sx={{
        boxShadow: 'inset 0 -1px 0 0 #d2d7de',
        pb: 2,
      }}
    >
      <FormControl variant="standard" fullWidth>
        <InputLabel shrink>Charge Groups</InputLabel>
        <Box
          sx={{
            mt: 3,
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            overflow: 'auto',
          }}
        >
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={indeterminate}
                  onChange={() => handleChange('all')}
                  sx={{ mr: 1 }}
                />
              }
              label={t('list.filter.all')}
              sx={{ py: 0.5, pl: 1.5 }}
            />
            {data?.map(group => (
              <FormControlLabel
                key={group.id}
                control={
                  <Checkbox
                    checked={currentValue.includes(group?.id || 0)}
                    onChange={() => handleChange(group?.id || 0)}
                    sx={{ mr: 1 }}
                  />
                }
                label={group.name}
                sx={{ py: 0.5, pl: 1.5 }}
              />
            ))}
          </FormGroup>
        </Box>
      </FormControl>
    </Box>
  );
};
